<template>
  <div
    v-if="sidebarOpen && userType !== 'admin' && userType !== 'unassigned' && credentials.length > 0"
    class="relative mb-4"
  >
    <!-- Current Credential Display -->
    <button
      @click="toggleDropdown"
      class="w-full px-3 py-2 bg-blue-100 rounded-lg border border-blue-300 hover:bg-blue-200 transition-colors text-left flex items-center justify-between"
      :class="{ 'ring-2 ring-blue-400': dropdownOpen }"
    >
      <div class="flex-1">
        <p class="text-xs text-blue-600 font-semibold">Credential</p>
        <p class="text-sm text-blue-900 truncate">{{ currentCredentialName }}</p>
      </div>
      <ChevronDownIcon
        :class="[
          'w-4 h-4 text-blue-600 transition-transform flex-shrink-0 ml-2',
          dropdownOpen ? 'transform rotate-180' : ''
        ]"
      />
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="dropdownOpen"
      class="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-300 max-h-64 overflow-y-auto"
    >
      <div v-if="loading" class="p-4 text-center text-gray-500 text-sm">
        Loading credentials...
      </div>
      <div v-else-if="error" class="p-4 text-center text-red-600 text-sm">
        {{ error }}
      </div>
      <div v-else-if="credentials.length === 0" class="p-4 text-center text-gray-500 text-sm">
        No credentials assigned
      </div>
      <button
        v-for="credential in credentials"
        :key="credential.id"
        @click="switchTo(credential.id)"
        :disabled="switching"
        :class="[
          'w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors flex items-center justify-between',
          credential.is_current ? 'bg-blue-50' : '',
          switching ? 'opacity-50 cursor-not-allowed' : ''
        ]"
      >
        <div class="flex-1">
          <p class="text-sm font-medium text-gray-900">{{ credential.name }}</p>
          <p v-if="credential.description" class="text-xs text-gray-600 truncate">
            {{ credential.description }}
          </p>
        </div>
        <div class="flex items-center space-x-1 flex-shrink-0 ml-2">
          <span
            v-if="credential.is_primary"
            class="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full"
            title="Primary credential (default on login)"
          >
            Primary
          </span>
          <span
            v-if="credential.is_current"
            class="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full"
          >
            Active
          </span>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { getUserCredentials, switchCredential } from '@/api/auth';
import { useAuth } from '@/stores/auth';

/* eslint-disable no-undef */
const props = defineProps({
  sidebarOpen: {
    type: Boolean,
    required: true,
  },
});

const auth = useAuth();
const userType = computed(() => auth.user.value?.user_type?.toLowerCase() || null);

const dropdownOpen = ref(false);
const credentials = ref([]);
const loading = ref(false);
const switching = ref(false);
const error = ref('');

const currentCredentialName = computed(() => {
  const current = credentials.value.find((c) => c.is_current);
  return current ? current.name : 'Loading...';
});

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
  if (dropdownOpen.value && credentials.value.length === 0) {
    loadCredentials();
  }
};

const loadCredentials = async () => {
  loading.value = true;
  error.value = '';
  try {
    const response = await getUserCredentials();
    credentials.value = response.data;
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to load credentials';
    console.error('Failed to load credentials:', err);
  } finally {
    loading.value = false;
  }
};

const switchTo = async (credentialId) => {
  // Don't switch if already current
  const credential = credentials.value.find((c) => c.id === credentialId);
  if (credential?.is_current) {
    dropdownOpen.value = false;
    return;
  }

  switching.value = true;
  error.value = '';
  try {
    const response = await switchCredential(credentialId);
    
    // Update auth store with new token using the login method
    const { access_token, refresh_token } = response.data;
    auth.login(access_token, refresh_token);
    
    // Force a hard reload to ensure new token is used
    // Using href assignment instead of reload() to bypass cache
    window.location.href = window.location.pathname;
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to switch credential';
    console.error('Failed to switch credential:', err);
  } finally {
    switching.value = false;
  }
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (dropdownOpen.value && !event.target.closest('.relative')) {
    dropdownOpen.value = false;
  }
};

// Load credentials on mount
onMounted(() => {
  loadCredentials();
  document.addEventListener('click', handleClickOutside);
});

// Clean up event listener
import { onBeforeUnmount } from 'vue';
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Watch sidebar open state - close dropdown when sidebar closes
watch(() => props.sidebarOpen, (newVal) => {
  if (!newVal) {
    dropdownOpen.value = false;
  }
});
</script>
